import { useEffect, useState, useCallback } from 'react';
import { Star, MessageSquarePlus, Quote } from 'lucide-react';
import { Reveal } from './Reveal';
import { ReviewDialog } from './ReviewDialog';
import { fetchReviews } from '../lib/api';

const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diffSec = Math.max(0, (Date.now() - then) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} day${diffSec >= 86400 * 2 ? 's' : ''} ago`;
    if (diffSec < 86400 * 30) return `${Math.floor(diffSec / (86400 * 7))} week${diffSec >= 86400 * 14 ? 's' : ''} ago`;
    if (diffSec < 86400 * 365) return `${Math.floor(diffSec / (86400 * 30))} month${diffSec >= 86400 * 60 ? 's' : ''} ago`;
    return `${Math.floor(diffSec / (86400 * 365))} year${diffSec >= 86400 * 730 ? 's' : ''} ago`;
};

export const Testimonials = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const data = await fetchReviews();
            setReviews(Array.isArray(data) ? data : []);
        } catch {
            setReviews([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const count = reviews.length;
    const avg = count ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / count : 0;
    const avgDisplay = avg ? avg.toFixed(2) : '—';

    return (
        <section
            data-testid="testimonials-section"
            className="py-24 md:py-32 bg-muted relative overflow-hidden"
        >
            <div className="container-axion">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-14">
                    <Reveal className="md:col-span-7">
                        <div className="overline">Reviews</div>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-medium tracking-tight mt-4 leading-[1.05]">
                            Spoken about<br />in <em className="font-light italic text-accent">whispers.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={120} className="md:col-span-5 md:pb-3 space-y-4">
                        {count > 0 && (
                            <div className="inline-flex items-center gap-3 bg-white border border-border rounded-full px-5 py-3">
                                <div className="flex items-center gap-1">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <Star
                                            key={i}
                                            className={`w-3.5 h-3.5 ${i < Math.round(avg) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`}
                                        />
                                    ))}
                                </div>
                                <span className="font-outfit text-sm text-foreground" data-testid="reviews-average">{avgDisplay}</span>
                                <span className="text-xs text-muted-foreground" data-testid="reviews-count">· {count} review{count === 1 ? '' : 's'}</span>
                            </div>
                        )}
                        <p className="text-muted-foreground text-[15px] leading-relaxed">
                            Real feedback from real Bristol clients. If we cleaned for you, we'd love to hear what you thought.
                        </p>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="open-review-dialog-button"
                            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-outfit text-sm px-6 py-3 hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-12px_rgba(47,130,105,0.55)] transition-all"
                        >
                            <MessageSquarePlus className="w-4 h-4" strokeWidth={1.8} />
                            Leave a review
                        </button>
                    </Reveal>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="reviews-loading">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="bg-white border border-border rounded-2xl p-6 animate-pulse h-48">
                                <div className="h-3 w-20 bg-muted rounded mb-4" />
                                <div className="h-3 w-full bg-muted rounded mb-2" />
                                <div className="h-3 w-4/5 bg-muted rounded mb-2" />
                                <div className="h-3 w-3/5 bg-muted rounded" />
                            </div>
                        ))}
                    </div>
                ) : count === 0 ? (
                    <Reveal>
                        <div
                            data-testid="reviews-empty-state"
                            className="bg-white border border-dashed border-border rounded-2xl p-12 md:p-16 text-center max-w-2xl mx-auto"
                        >
                            <span className="grid place-items-center w-14 h-14 rounded-full bg-accent/20 text-primary mx-auto">
                                <Quote className="w-6 h-6" strokeWidth={1.6} />
                            </span>
                            <h3 className="font-outfit text-2xl md:text-3xl mt-5 tracking-tight">
                                Be the first to review.
                            </h3>
                            <p className="text-muted-foreground mt-3 text-[15px] leading-relaxed max-w-md mx-auto">
                                We've just opened our reviews page. If you've used Axion, share your experience —
                                it takes thirty seconds and helps the next neighbour pick the right cleaner.
                            </p>
                            <button
                                onClick={() => setOpen(true)}
                                data-testid="empty-state-leave-review"
                                className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-outfit text-sm px-6 py-3 hover:-translate-y-0.5 transition-transform"
                            >
                                <MessageSquarePlus className="w-4 h-4" strokeWidth={1.8} />
                                Leave the first review
                            </button>
                        </div>
                    </Reveal>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="reviews-grid">
                        {reviews.map((r, idx) => (
                            <Reveal
                                key={r.id}
                                delay={Math.min(idx, 6) * 80}
                                className="bg-white border border-border rounded-2xl p-6 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-500"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: r.rating || 0 }).map((_, i) => (
                                            <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                        ))}
                                    </div>
                                    <Quote className="w-4 h-4 text-accent" strokeWidth={1.6} />
                                </div>
                                <p className="text-[15px] leading-relaxed text-foreground/90">"{r.text}"</p>
                                <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                                    <div>
                                        <div className="font-outfit text-sm">{r.name}</div>
                                        {r.location && (
                                            <div className="text-xs text-muted-foreground">{r.location}</div>
                                        )}
                                    </div>
                                    <span className="text-[11px] text-muted-foreground">{formatRelative(r.created_at)}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>

            <ReviewDialog open={open} onOpenChange={setOpen} onSubmitted={() => { setOpen(false); load(); }} />
        </section>
    );
};
